import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { register } from '@/api';
import { setToken } from '@/api/client';
import AgreementModal from '@/components/feature/AgreementModal';

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referral, setReferral] = useState('');

  //从邀请链接自动填充推荐码（#/register?invite_code=xxx）
  useEffect(() => {
    const code = searchParams.get('invite_code') || searchParams.get('referral');
    if (code) setReferral(code);
  }, [searchParams]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toast, setToast] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [modal, setModal] = useState<'service' | 'privacy' | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || !password.trim() || !confirmPassword.trim() || !referral.trim()) {
      setToast('请填写所有必填项');
      setTimeout(() => setToast(''), 2000);
      return;
    }
    if (password !== confirmPassword) {
      setToast('两次输入的密码不一致');
      setTimeout(() => setToast(''), 2000);
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      //注册成功直接登录，保存 token 跳首页
      const res = await register(phone.trim(), password, confirmPassword, referral.trim());
      setToken(res.token);
      setToast('注册成功');
      setTimeout(() => {
        setToast('');
        navigate('/');
      }, 800);
    } catch (err) {
      setToast(err instanceof Error ? err.message : '注册失败');
      setTimeout(() => setToast(''), 2000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mobile-container bg-white min-h-screen flex flex-col">
      {/* Top bar: back arrow */}
      <div className="flex items-center px-4 h-12">
        <button
          onClick={() => navigate(-1)}
          className="w-8 h-8 flex items-center justify-center cursor-pointer active:scale-95 transition-transform"
          aria-label="返回"
        >
          <i className="ri-arrow-left-s-line text-foreground-800 text-xl" />
        </button>
      </div>

      {/* Logo area */}
      <div className="flex flex-col items-center mt-2 mb-8">
        <div className="relative">
          <div className="absolute inset-0 bg-primary-400/25 rounded-3xl blur-xl scale-125" />
          <div className="relative w-16 h-16 rounded-2xl bg-primary-500 flex items-center justify-center">
            <span className="text-white text-2xl font-bold tracking-wider">众</span>
          </div>
        </div>
        <h1 className="mt-4 text-foreground-950 text-xl font-bold tracking-wide">众泰成</h1>
        <p className="mt-1 text-foreground-400 text-sm tracking-wider">创建新账号</p>
      </div>

      {/* Register form */}
      <form onSubmit={handleRegister} className="px-6 flex-1">
        <h2 className="text-foreground-950 text-lg font-bold mb-6">注册账号</h2>

        {/* Phone input */}
        <div className="relative mb-4">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400">
            <i className="ri-smartphone-line text-lg" />
          </div>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="请输入手机号"
            maxLength={11}
            className="w-full h-12 pl-12 pr-4 rounded-xl bg-white border border-background-300 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-all"
          />
        </div>

        {/* Password input */}
        <div className="relative mb-4">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400">
            <i className="ri-shield-keyhole-line text-lg" />
          </div>
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="请输入登录密码"
            className="w-full h-12 pl-12 pr-12 rounded-xl bg-white border border-background-300 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-all"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400 cursor-pointer hover:text-foreground-600 transition-colors"
            aria-label={showPassword ? '隐藏密码' : '显示密码'}
          >
            <i className={`${showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} text-lg`} />
          </button>
        </div>

        {/* Confirm password input */}
        <div className="relative mb-4">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400">
            <i className="ri-shield-check-line text-lg" />
          </div>
          <input
            type={showConfirm ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="请确认登录密码"
            className="w-full h-12 pl-12 pr-12 rounded-xl bg-white border border-background-300 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-all"
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400 cursor-pointer hover:text-foreground-600 transition-colors"
            aria-label={showConfirm ? '隐藏密码' : '显示密码'}
          >
            <i className={`${showConfirm ? 'ri-eye-line' : 'ri-eye-off-line'} text-lg`} />
          </button>
        </div>

        {/* Referral code input */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400">
              <i className="ri-gift-line text-lg" />
            </div>
            <input
              type="text"
              value={referral}
              onChange={(e) => setReferral(e.target.value)}
              placeholder="请输入推荐码"
              className="w-full h-12 pl-12 pr-4 rounded-xl bg-white border border-background-300 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-all"
            />
          </div>
          <p className="mt-2 text-foreground-400 text-xs leading-relaxed">须凭推荐码方可加入，推荐码绑定后不可更改</p>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full h-12 rounded-xl bg-primary-500 text-white text-sm font-bold flex items-center justify-center gap-2 cursor-pointer hover:bg-primary-600 active:scale-[0.98] transition-all shadow-lg shadow-primary-500/20 disabled:opacity-60"
        >
          {submitting ? '注册中...' : '立即注册'}
          <i className="ri-arrow-right-line text-sm" />
        </button>

        {/* Login link */}
        <div className="flex items-center justify-center mt-8 gap-2">
          <span className="text-foreground-400 text-sm">已有账号?</span>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-primary-500 text-sm font-bold cursor-pointer hover:text-primary-600 transition-colors"
          >
            立即登录
          </button>
        </div>
      </form>

      {/* Bottom agreement */}
      <div className="px-6 pb-8 pt-4 mt-auto">
        <p className="text-center text-foreground-400 text-xs leading-relaxed">
          注册即表示同意
          <button
            type="button"
            onClick={() => setModal('service')}
            className="text-primary-500 cursor-pointer hover:underline mx-0.5"
          >
            《用户服务协议》
          </button>
          和
          <button
            type="button"
            onClick={() => setModal('privacy')}
            className="text-primary-500 cursor-pointer hover:underline mx-0.5"
          >
            《隐私政策》
          </button>
        </p>
      </div>

      {/* 协议/隐私政策弹层 */}
      <AgreementModal type={modal} onClose={() => setModal(null)} onError={showToast} />

      {/* Toast */}
      {toast && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-sm rounded-2xl text-white text-sm z-50 shadow-xl">
          {toast}
        </div>
      )}
    </div>
  );
}